// Angebote ohne Oberfläche: Antworten des Servers (server/src/modules/rechnungstool/angebote.ts), Adressen und was die
// Liste daraus rechnet. Benutzt von Angebote.tsx, AngebotForm.tsx und RechnungForm.tsx (Rechnung aus einem Angebot).
// Eine Zeile der Liste wie bei den Rechnungen (RechnungZeile.ts): höchstens eine graue Zeile (angebotInfo), ein Knopf für
// den nächsten Schritt (angebotSchritt) und das Menü „···“ mit allem anderen (angebotMenue). Nur Daten, kein React und
// kein Tarif: die Rechte kommen im Kontext. Keine E-Mail-Adressen und keine langen Nachrichten in der Liste (die stehen
// in den Fenstern „Per E-Mail“ und „Antwort des Kunden“).
import { kurzTag } from '../lib/format';
import { neuerLieferschein } from './AuftragDaten';
import type { EFormat, Kunde, Position, RtArt, RtItem, Versand } from './types';
import type { Waehrung } from './waehrung';

export type AngebotStatus = 'offen' | 'angenommen' | 'abgelehnt';
// Status in der Liste: offen und „gültig bis“ vorbei → abgelaufen
export type AngebotAnzeige = AngebotStatus | 'abgelaufen';

// Antwort des Kunden über den Link aus der E-Mail (server/src/modules/rechnungstool/annahme.ts); danach bleibt der
// Status fest. nachricht: Nachricht (angenommen) bzw. Grund (abgelehnt). abAuto: ob die Auftragsbestätigung
// automatisch kommen sollte.
export interface AngebotOnline {
  art: 'angenommen' | 'abgelehnt';
  am: string;
  name: string | null;
  referenz: string | null;
  nachricht: string | null;
  abAuto: boolean;
}

export interface AngebotZeile {
  id: string;
  number: string;
  date: string;
  validUntil: string;
  // Name des Kunden auf dem Angebot
  customer: string;
  kundeId: string | null;
  site: string | null;
  netto: number;
  betrag: number;
  // Beträge in dieser Währung; Kurs (Euro je Einheit) für die Summen in Euro
  waehrung: Waehrung;
  kurs: number;
  status: AngebotStatus;
  anzeige: AngebotAnzeige;
  statusAt: string | null;
  // Rechnung oder Schlussrechnung daraus (storniert: das Angebot ist wieder frei)
  rechnung: { id: string; number: string; status: RtItem['status']; art: RtArt } | null;
  // gültige Auftragsbestätigung dazu (dann gehen Status, Lieferschein und Rechnung über sie)
  auftrag: { id: string; number: string; date: string } | null;
  online: AngebotOnline | null;
  // online angenommen, die Auftragsbestätigung sollte automatisch kommen, es gibt aber keine
  abFehlt: boolean;
  versand: Versand[];
}

// Ein Angebot mit allem zum Kopieren (GET /angebot/:id): Kunde mit Anschrift statt nur dem Namen
export type AngebotVoll = Omit<AngebotZeile, 'customer'> & {
  customer: Kunde;
  items: Position[];
  intro: string | null;
  note: string | null;
  // Hinweis bei 0 % USt (fehlt bei älteren Angeboten)
  steuerHinweis?: string | null;
  kleinunternehmer: boolean;
  ust: number;
};

// Rechnungsformular aus dem Angebot (GET /angebot/:id/rechnung): vorbefüllte Felder und bisherige Abschläge
export interface AngebotRechnung {
  angebot: { id: string; number: string; date: string; validUntil: string; netto: number; betrag: number };
  bisher: { id: string; number: string; invoiceDate: string; netto: number; betrag: number; prozent: number }[];
  prozentBisher: number;
  warnung: string | null;
  form: {
    angebotId: string;
    kundeId: string | null;
    customer: Kunde;
    eFormat: EFormat;
    art: RtArt;
    auftrag: string | null;
    abschlaege: string[];
    items: Position[];
    site: string | null;
    paymentDays: number;
    note: string;
    // Hinweis bei 0 % USt des Angebots (fehlt bei älteren Angeboten)
    steuerHinweis?: string | null;
    // Währung des Angebots (den Kurs schlägt das Formular zum Rechnungsdatum neu vor)
    waehrung: Waehrung;
  };
}

export const angeboteUrl = (firmId: string) => `/api/rechnungstool/${firmId}/angebote`;
export const angebotUrl = (id: string) => `/api/rechnungstool/angebot/${id}`;
// Einstellung „Auftragsbestätigung automatisch“ ({ abAuto })
export const angebotEinstellungUrl = (firmId: string) => `/api/rechnungstool/${firmId}/einstellungen/angebot`;
export const angebotPdfUrl = (id: string) => `${angebotUrl(id)}/pdf`;
export const angebotRechnungUrl = (id: string, art: RtArt = 'normal', prozent?: number) =>
  `${angebotUrl(id)}/rechnung?art=${art}${prozent != null ? `&prozent=${prozent}` : ''}`;
// Rechnungsformular mit den Angaben des Angebots (RechnungForm.tsx liest ?angebot=…&art=…&prozent=…)
export const rechnungAusAngebot = (id: string, art: RtArt = 'normal', prozent?: number) =>
  `/rechnung/neu?angebot=${id}${art !== 'normal' ? `&art=${art}` : ''}${prozent != null ? `&prozent=${prozent}` : ''}`;

// Aus dem Angebot ist eine Rechnung (oder Schlussrechnung) geschrieben, die nicht storniert ist
export const abgerechnet = (g: AngebotZeile) => !!g.rechnung && g.rechnung.status !== 'storniert';

// Abschlagsrechnungen zum Angebot wie auf dem Server (abschlaegeZumAngebot): Auftrag nennt die Angebotsnummer,
// derselbe Kunde, nicht storniert
export const abschlaegeZu = (g: AngebotZeile, items: RtItem[]) =>
  items.filter(
    (i) =>
      i.kind === 'rechnung' &&
      i.art === 'abschlag' &&
      i.status !== 'storniert' &&
      (i.auftrag ?? '').includes(g.number) &&
      (!g.kundeId || i.kundeId === g.kundeId),
  );

export const ANZEIGE_NAME: Record<AngebotAnzeige, string> = {
  offen: 'Offen',
  abgelaufen: 'Abgelaufen',
  angenommen: 'Angenommen',
  abgelehnt: 'Abgelehnt',
};

// Rechte des Logins (Billtoro: darf, in der Kanzlei-App alles): „Angebote und Aufträge“ bzw. „Rechnungen“
export interface AngebotKontext {
  bieten: boolean;
  rechnen: boolean;
}

// Was ein Knopf oder Eintrag tut: ein Link zu einem Formular bzw. einer Liste, ein Fenster (per E-Mail, Rückfrage
// „Auftrag bestätigen“, Abschlag, Antwort des Kunden) oder der Status von Hand (label: Name zum Vorlesen)
export type AngebotAktion =
  | { art: 'link'; text: string; ziel: string }
  | { art: 'mail' | 'auftrag' | 'abschlag' | 'antwort'; text: string }
  | { art: 'status'; text: string; status: AngebotStatus; label?: string };
export type AngebotEintrag = AngebotAktion | { art: 'trenner' };

// weder abgerechnet noch abgelehnt: daraus kann noch etwas werden
const frei = (g: AngebotZeile) => !abgerechnet(g) && g.status !== 'abgelehnt';
// online beantwortet oder mit Auftragsbestätigung: der Status bleibt, wie er ist
const fest = (g: AngebotZeile) => !!g.online || !!g.auftrag;

const MAIL: AngebotAktion = { art: 'mail', text: 'Per E-Mail' };
const AUFTRAG: AngebotAktion = { art: 'auftrag', text: 'Auftrag bestätigen' };
const markieren = (g: AngebotZeile, status: 'angenommen' | 'abgelehnt'): AngebotAktion => ({
  art: 'status',
  text: ANZEIGE_NAME[status],
  status,
  label: `Angebot ${g.number} als ${status} markieren`,
});
const zumAuftrag = (ab: { number: string }): AngebotAktion => ({
  art: 'link',
  text: 'Zum Auftrag',
  ziel: `/rechnung/auftraege?suche=${encodeURIComponent(ab.number)}`,
});
const rechnung = (g: AngebotZeile): AngebotAktion => ({ art: 'link', text: 'Rechnung schreiben', ziel: rechnungAusAngebot(g.id) });
const schluss = (g: AngebotZeile): AngebotAktion => ({ art: 'link', text: 'Schlussrechnung', ziel: rechnungAusAngebot(g.id, 'schluss') });

// Der eine Knopf der Zeile (erste passende Regel). abschlaege: Nummern der Abschlagsrechnungen zum Angebot
// (abschlaegeZu). Abgerechnet oder abgelehnt: keiner; fehlt die Auftragsbestätigung nach der Antwort online, sie;
// noch nie verschickt oder abgelaufen: per E-Mail (nachfassen); offen: angenommen; sonst Schlussrechnung, zum Auftrag,
// Rechnung oder Auftrag bestätigen. Ohne das Recht kein Knopf.
export function angebotSchritt(g: AngebotZeile, abschlaege: string[], ctx: AngebotKontext): AngebotAktion | null {
  if (!frei(g)) return null;
  if (g.abFehlt && ctx.bieten) return AUFTRAG;
  if (g.anzeige === 'offen' && !g.versand.length && ctx.bieten) return MAIL;
  if (g.anzeige === 'abgelaufen' && ctx.bieten) return MAIL;
  if (g.status === 'offen' && !fest(g) && ctx.bieten) return markieren(g, 'angenommen');
  if (abschlaege.length && ctx.rechnen) return schluss(g);
  if (g.auftrag) return zumAuftrag(g.auftrag);
  if (ctx.rechnen) return rechnung(g);
  if (ctx.bieten) return AUFTRAG;
  return null;
}

// Nachricht des Kunden in der Zeile: eine Zeile, höchstens 60 Zeichen, möglichst am Wortende gekürzt (ganz im Fenster
// „Antwort des Kunden“)
function kurz(t: string, max = 60): string {
  const z = t.replace(/\s+/g, ' ').trim();
  if (z.length <= max) return z;
  const teil = z.slice(0, max - 1);
  const wort = teil.lastIndexOf(' ');
  return `${(wort > max / 2 ? teil.slice(0, wort) : teil).trimEnd()}…`;
}

// Graue Zeile unter dem Kunden: Stand (Rechnung, Antwort online, Status von Hand oder gültig bis) und ein Zusatz (erste
// passende Angabe: fehlende Auftragsbestätigung, Nachricht, Auftrag, Abschläge, stornierte Rechnung, Objekt,
// Bestellnummer). Bei offenen Angeboten kommt das Objekt vor den Abschlägen und der stornierten Rechnung (z. B. nach
// „Zurück auf offen“), sonst sagt der Vorgang mehr. Die Bestellnummer steht sonst im Fenster „Antwort des Kunden“.
// Tage kurz (kurzTag). warn: die Auftragsbestätigung fehlt. null: keine Zeile.
export function angebotInfo(g: AngebotZeile, abschlaege: string[], today: string): { teile: string[]; warn: boolean } | null {
  const tag = (v: string) => kurzTag(v, today);
  const o = g.online;
  const r = g.rechnung;
  const rName = r?.art === 'schluss' ? 'Schlussrechnung' : 'Rechnung';
  const stand =
    r && abgerechnet(g)
      ? `${rName} ${r.number}`
      : o?.art === 'angenommen'
        ? `online angenommen${o.name ? ` von ${o.name}` : ''} am ${tag(o.am)}`
        : o
          ? `online abgelehnt am ${tag(o.am)}${o.name ? ` von ${o.name}` : ''}`
          : g.status === 'offen'
            ? `${g.anzeige === 'abgelaufen' ? 'war gültig' : 'gültig'} bis ${tag(g.validUntil)}`
            : g.statusAt
              ? `${g.status} am ${tag(g.statusAt)}`
              : null;
  const objekt = g.site ? `Objekt ${g.site}` : null;
  const zusatz = [
    o?.nachricht ? `${o.art === 'angenommen' ? 'Nachricht' : 'Grund'}: „${kurz(o.nachricht)}“` : null,
    g.auftrag ? `Auftrag ${g.auftrag.number}` : null,
    g.status === 'offen' ? objekt : null,
    abschlaege.length ? `${abschlaege.length === 1 ? 'Abschlagsrechnung' : 'Abschlagsrechnungen'} ${abschlaege.join(', ')}` : null,
    r && r.status === 'storniert' ? `${rName} ${r.number} storniert` : null,
    objekt,
    o?.referenz ? `Bestellnummer ${o.referenz}` : null,
  ].find((t): t is string => !!t);
  const teile = [stand, g.abFehlt ? 'Auftragsbestätigung fehlt' : (zusatz ?? null)].filter((t): t is string => !!t);
  return teile.length ? { teile, warn: g.abFehlt } : null;
}

// Menü „···“: alles, was nicht der Knopf ist, mit den Bedingungen wie früher in der Zeile (die PDF ist der Klick auf
// die Zeile). Reihenfolge: verschicken, Status, Antwort, schreiben, kopieren | zurück auf offen. Ein leeres Menü zeigt
// die Zeile nicht.
export function angebotMenue(g: AngebotZeile, abschlaege: string[], ctx: AngebotKontext, schritt: AngebotAktion | null): AngebotEintrag[] {
  const e: AngebotEintrag[] = [];
  const dazu = (a: AngebotAktion) => {
    if (schritt?.text !== a.text) e.push(a);
  };
  if (g.status !== 'abgelehnt' && ctx.bieten) dazu(MAIL);
  if (g.status === 'offen' && !fest(g) && ctx.bieten) {
    dazu(markieren(g, 'angenommen'));
    dazu(markieren(g, 'abgelehnt'));
  }
  // mit Nachricht, Grund oder Bestellnummer (die Bestellnummer fehlt in der Zeile, sobald ein anderer Zusatz passt)
  if (g.online?.nachricht || g.online?.referenz) dazu({ art: 'antwort', text: 'Antwort des Kunden' });
  if (frei(g) && !g.auftrag && ctx.bieten) {
    dazu(AUFTRAG);
    dazu({ art: 'link', text: 'Lieferschein', ziel: neuerLieferschein({ art: 'angebot', id: g.id }) });
  }
  if (frei(g) && !abschlaege.length) {
    if (g.auftrag) dazu(zumAuftrag(g.auftrag));
    else if (ctx.rechnen) dazu(rechnung(g));
    // Abschlags- und Schlussrechnungen nur in Euro
    if (g.waehrung === 'EUR' && ctx.rechnen) dazu({ art: 'abschlag', text: 'Abschlagsrechnung' });
  }
  if (frei(g) && abschlaege.length && ctx.rechnen) {
    dazu(schluss(g));
    if (g.waehrung === 'EUR') dazu({ art: 'abschlag', text: 'Weiterer Abschlag' });
  }
  if (ctx.bieten) dazu({ art: 'link', text: 'Kopieren', ziel: `/rechnung/angebote/neu?vorlage=${g.id}` });
  if (g.status !== 'offen' && !abgerechnet(g) && !fest(g) && ctx.bieten) {
    if (e.length) e.push({ art: 'trenner' });
    e.push({ art: 'status', text: 'Zurück auf offen', status: 'offen' });
  }
  return e;
}
